import { Row } from '@tanstack/react-table'

import { SimpleTableBodyType } from './SimpleTable'

export function extractSortValue(value: unknown): unknown {
  if (!value || typeof value !== 'object') {
    return value
  }

  // Handle {value, display} pattern
  if ('display' in value && 'value' in value && !('configuration' in value)) {
    return (value as { value: unknown }).value
  }

  // Handle DrilldownCell
  if ('value' in value && 'configuration' in value) {
    return (value as { value: unknown }).value
  }

  // Handle AddressCell
  if ('address' in value) {
    return (value as { address: string }).address
  }

  // Handle DateCell
  if ('date' in value) {
    return (value as { date: Date | string }).date
  }

  // Handle React elements
  if ('props' in value) {
    const element = value as { props?: { children?: unknown } }
    if (element.props?.children) {
      return element.props.children
    }
  }

  return value
}

export function parseDuration(str: string): number {
  let totalMinutes = 0

  const months = str.match(/(\d+)\s*mths?/)?.[1]
  if (months) totalMinutes += parseInt(months) * 30 * 24 * 60

  const weeks = str.match(/(\d+)\s*wks?/)?.[1]
  if (weeks) totalMinutes += parseInt(weeks) * 7 * 24 * 60

  const days = str.match(/(\d+)\s*days?/)?.[1]
  if (days) totalMinutes += parseInt(days) * 24 * 60

  const hours = str.match(/(\d+)\s*hrs?/)?.[1]
  if (hours) totalMinutes += parseInt(hours) * 60

  const minutes = str.match(/(\d+)\s*mins?/)?.[1]
  if (minutes) totalMinutes += parseInt(minutes)

  return totalMinutes
}

export function isDuration(str: string): boolean {
  return /\b(mth|wk|day|hr|min|sec)s?\b/.test(str)
}

/**
 * Compare two values for sorting, with smart type detection
 */
export function compareValues(aVal: unknown, bVal: unknown): number {
  // Handle null/undefined
  if (aVal == null && bVal == null) return 0
  if (aVal == null) return 1
  if (bVal == null) return -1

  const aStr = String(aVal)
  const bStr = String(bVal)

  if (isDuration(aStr) && isDuration(bStr)) {
    return parseDuration(aStr) - parseDuration(bStr)
  }

  const cleanAStr = aStr.replace(/,/g, '')
  const cleanBStr = bStr.replace(/,/g, '')
  const aNum = parseFloat(cleanAStr)
  const bNum = parseFloat(cleanBStr)

  if (!isNaN(aNum) && !isNaN(bNum)) {
    return aNum - bNum
  }

  const aDate = new Date(aVal as string | number | Date)
  const bDate = new Date(bVal as string | number | Date)

  if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
    return aDate.getTime() - bDate.getTime()
  }

  return aStr.localeCompare(bStr, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function createSortingFn(headerKey: string) {
  return (rowA: Row<SimpleTableBodyType>, rowB: Row<SimpleTableBodyType>) => {
    const aVal = extractSortValue(rowA.original[headerKey])
    const bVal = extractSortValue(rowB.original[headerKey])
    return compareValues(aVal, bVal)
  }
}
